import React, { useState, useEffect } from 'react';
import { Card, Table, Tag, Button, Space, Modal, Form, Input, Select, DatePicker, message, Row, Col, Statistic, InputNumber } from 'antd';
import { SearchOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { get, post } from '../../utils/merchantApi';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Option } = Select;

const TRANSACTION_TYPE_MAP = {
  recharge: { color: 'green', text: '收款' },
  withdraw: { color: 'red', text: '代付' },
  trade: { color: 'blue', text: '交易' },
  commission: { color: 'purple', text: '佣金' },
  activity: { color: 'cyan', text: '活动' },
  adjustment: { color: 'orange', text: '调整' },
  balance_freeze: { color: 'default', text: '冻结/解冻' },
  merchant_recharge: { color: 'green', text: '商户充值' },
  merchant_withdraw: { color: 'red', text: '商户提现' }
};

const MerchantAccount = () => {
  const [loading, setLoading] = useState(false);
  const [accountInfo, setAccountInfo] = useState({});
  const [dataSource, setDataSource] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, total_amount: 0, page: 1, limit: 20 });
  const [filters, setFilters] = useState({});
  const [rechargeVisible, setRechargeVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPaymentAccount, setSelectedPaymentAccount] = useState(null);
  const [rechargeForm] = Form.useForm();

  useEffect(() => {
    loadAccountInfo();
    loadTransactions();
    loadPaymentMethods();
  }, [pagination.page]);

  const loadPaymentMethods = async () => {
    try {
      const response = await get('/merchant/account/payment-methods');
      setPaymentMethods(response.data || []);
    } catch (error) {
      console.error('获取支付方式失败:', error);
    }
  };

  const loadAccountInfo = async () => {
    try {
      const response = await get('/merchant/account');
      setAccountInfo(response.data || {});
    } catch (error) {
      console.error('获取账户信息失败:', error);
    }
  };

  const loadTransactions = async (params = {}) => {
    setLoading(true);
    try {
      const response = await get('/merchant/account/transactions', {
        page: pagination.page,
        limit: pagination.limit,
        ...filters,
        ...params
      });

      setDataSource(response.data?.list || []);
      setPagination({
        ...pagination,
        total: response.data?.pagination?.total || 0,
        total_amount: response.data?.pagination?.total_amount || 0,
        page: response.data?.pagination?.page || 1
      });
    } catch (error) {
      message.error('加载数据失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (values) => {
    const newFilters = {
      ...values,
      startDate: values.dateRange?.[0]?.format('YYYY-MM-DD'),
      endDate: values.dateRange?.[1]?.format('YYYY-MM-DD')
    };
    delete newFilters.dateRange;
    setFilters(newFilters);
    setPagination({ ...pagination, page: 1 });
    loadTransactions({ ...newFilters, page: 1 });
  };

  const handleReset = () => {
    setFilters({});
    setPagination({ ...pagination, page: 1 });
    loadTransactions({ page: 1 });
  };

  const handleRecharge = async (values) => {
    setSubmitting(true);
    try {
      await post('/merchant/account/recharge', {
        amount: values.amount,
        payment_account_id: values.payment_account_id
      });

      message.success('收款订单提交成功，请等待审核');
      setRechargeVisible(false);
      rechargeForm.resetFields();
      setSelectedPaymentAccount(null);
      loadAccountInfo();
    } catch (error) {
      message.error(error.message || '提交失败');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePaymentAccountChange = (accountId) => {
    const account = paymentMethods.find(p => p.id === accountId);
    setSelectedPaymentAccount(account);
  };

  const getTransactionTypeTag = (type) => {
    const config = TRANSACTION_TYPE_MAP[type] || { color: 'default', text: type };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const columns = [
    {
      title: '流水号',
      dataIndex: 'transaction_no',
      key: 'transaction_no',
      width: 200
    },
    {
      title: '类型',
      dataIndex: 'transaction_type',
      key: 'transaction_type',
      width: 100,
      render: (type) => getTransactionTypeTag(type)
    },
    {
      title: '金额',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      render: (val) => (
        <span style={{ color: val >= 0 ? '#52c41a' : '#ff4d4f', fontWeight: 500 }}>
          {val >= 0 ? '+' : ''}{Number(val).toFixed(2)}
        </span>
      )
    },
    {
      title: '余额变更',
      key: 'balance_change',
      width: 180,
      render: (_, record) => (
        <span>
          {Number(record.balance_before).toFixed(2)} → {Number(record.balance_after).toFixed(2)}
        </span>
      )
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      width: 250,
      ellipsis: true
    },
    {
      title: '时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (val) => val ? dayjs(val).format('YYYY-MM-DD HH:mm:ss') : '-'
    }
  ];

  return (
    <div>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card>
            <Statistic 
              title="账户余额" 
              value={accountInfo.balance || 0} 
              precision={2} 
              prefix=""
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic 
              title="冻结金额" 
              value={accountInfo.frozen_balance || 0} 
              precision={2} 
              prefix=""
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic 
              title="可用余额" 
              value={accountInfo.available_balance || 0} 
              precision={2} 
              prefix=""
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
        <Button 
          type="primary" 
          icon={<PlusOutlined />} 
          size="large" 
          block
          onClick={() => {
            loadPaymentMethods();
            setRechargeVisible(true);
          }}
        >
              充值
            </Button>
          </Card>
        </Col>
      </Row>

      <Card style={{ marginBottom: 16 }}>
        <Form layout="inline" onFinish={handleSearch}>
          <Form.Item name="type" label="类型">
            <Select placeholder="请选择" style={{ width: 120 }} allowClear>
              <Option value="recharge">收款</Option>
              <Option value="withdraw">代付</Option>
              <Option value="merchant_withdraw">商户提现</Option>
              <Option value="merchant_recharge">商户充值</Option>
              {/* <Option value="trade">交易</Option> */}
              {/* <Option value="commission">佣金</Option> */}
              {/* <Option value="activity">活动</Option> */}
              {/* <Option value="adjustment">调整</Option> */}
            </Select>
          </Form.Item>
          <Form.Item name="dateRange" label="日期">
            <RangePicker />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>搜索</Button>
          </Form.Item>
          <Form.Item>
            <Button onClick={handleReset}>重置</Button>
          </Form.Item>
          <Form.Item>
            <Button icon={<ReloadOutlined />} onClick={() => loadAccountInfo()}>刷新余额</Button>
          </Form.Item>
        </Form>
      </Card>

      <Card>
        {/* <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col>
            <Statistic title="流水总笔数" value={pagination.total} />
          </Col>
          <Col>
            <Statistic title="流水总金额" value={Number(pagination.total_amount || 0)} precision={2} prefix="¥" />
          </Col>
        </Row> */}

        <Table
          rowKey="id"
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条记录`,
            onChange: (page, pageSize) => {
              setPagination({ ...pagination, page, limit: pageSize });
            }
          }}
          scroll={{ x: 1000 }}
        />
      </Card>

      <Modal
        title="收款"
        open={rechargeVisible}
        onCancel={() => {
          setRechargeVisible(false);
          rechargeForm.resetFields();
          setSelectedPaymentAccount(null);
        }}
        footer={null}
        width={600}
      >
        <Form form={rechargeForm} layout="vertical" onFinish={handleRecharge}>
          <Form.Item name="amount" label="收款金额(请输入金额)" rules={[
            { required: true, message: '请输入收款金额' },
            { type: 'number', min: 1, message: '最低收款金额为1元' }
          ]}>
            <InputNumber
              style={{ width: '100%' }}
              placeholder="请输入收款金额"
              min={1}
              precision={2}
              addonAfter="元"
            />
          </Form.Item>
          <Form.Item name="payment_account_id" label="支付方式" rules={[{ required: true, message: '请选择支付方式' }]}>
            <Select 
              placeholder="请选择支付方式"
              onChange={handlePaymentAccountChange}
            >
              {paymentMethods.map(pm => (
                <Option key={pm.id} value={pm.id}>
                  {pm.account_name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          
          {selectedPaymentAccount && (
            <div style={{ 
              background: '#f5f5f5', 
              padding: 16, 
              borderRadius: 8, 
              marginBottom: 16 
            }}>
              <div style={{ fontWeight: 'bold', marginBottom: 8 }}>收款账户信息</div>
              {selectedPaymentAccount.payment_method === 'bank_transfer' && (
                <>
                  <div>银行名称：{selectedPaymentAccount.bank_name}</div>
                  <div>银行账号：{selectedPaymentAccount.bank_account}</div>
                  <div>账户持有人：{selectedPaymentAccount.account_holder}</div>
                </>
              )}
              {selectedPaymentAccount.payment_method === 'alipay' && (
                <>
                  <div>支付宝账号：{selectedPaymentAccount.alipay_account}</div>
                  {selectedPaymentAccount.qr_code && <div>二维码：<a href={selectedPaymentAccount.qr_code} target="_blank" rel="noopener noreferrer">查看</a></div>}
                </>
              )}
              {selectedPaymentAccount.payment_method === 'wechat' && (
                <>
                  <div>微信账号：{selectedPaymentAccount.wechat_account}</div>
                  {selectedPaymentAccount.qr_code && <div>二维码：<a href={selectedPaymentAccount.qr_code} target="_blank" rel="noopener noreferrer">查看</a></div>}
                </>
              )}
              {selectedPaymentAccount.payment_method === 'usdt' && (
                <>
                  <div>USDT地址：{selectedPaymentAccount.usdt_address}</div>
                  <div>网络类型：{selectedPaymentAccount.usdt_network}</div>
                  <div>当前汇率：{selectedPaymentAccount.exchange_rate} CNY/USDT</div>
                </>
              )}
              {selectedPaymentAccount.payment_method === 'm-pesa' && (
                <>
                  <div>手机号：{selectedPaymentAccount.mpesa_phone}</div>
                  <div>账户名：{selectedPaymentAccount.mpesa_name}</div>
                </>
              )}
              {selectedPaymentAccount.payment_method === 'mobile_money' && (
                <>
                  <div>手机号：{selectedPaymentAccount.mpesa_phone}</div>
                  <div>账户名：{selectedPaymentAccount.mpesa_name}</div>
                </>
              )}
            </div>
          )}
          
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={submitting}>提交收款</Button>
              <Button onClick={() => {
                setRechargeVisible(false);
                rechargeForm.resetFields();
                setSelectedPaymentAccount(null);
              }}>取消</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default MerchantAccount;
